import { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Wrench, X, Pencil, Trash2, Clock, Check,
  MapPin, Euro, FileText, Award, Loader2,
  Eye, EyeOff, CheckCircle, ChevronRight,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../../../config/api';
import { getAllMetiers, getPrestationsForMetier } from '../../../data/prestationsData';

const tarifTypeLabels = {
  forfait:     { label: 'À partir de',  icon: '💰', color: 'bg-green-100 text-green-700' },
  horaire:     { label: 'Tarif horaire', icon: '⏱️', color: 'bg-blue-100 text-blue-700' },
  metre_carre: { label: 'Au m²',         icon: '📐', color: 'bg-cyan-100 text-cyan-700' },
  sur_devis:   { label: 'Sur devis',     icon: '📋', color: 'bg-purple-100 text-purple-700' },
  gratuit:     { label: 'Gratuit',       icon: '🎁', color: 'bg-slate-100 text-slate-700' },
};

const certificationLabels = {
  rge:       { label: 'RGE',       description: 'Reconnu Garant de l\'Environnement' },
  qualibat:  { label: 'Qualibat',  description: 'Qualification Bâtiment' },
  qualifelec:{ label: 'Qualifelec',description: 'Qualification Électricité' },
  qualipac:  { label: 'QualiPAC', description: 'Qualification Pompe à Chaleur' },
  qualigaz:  { label: 'Qualigaz',  description: 'Qualification Gaz' },
  nf:        { label: 'NF',        description: 'Norme Française' },
  ce:        { label: 'CE',        description: 'Conformité Européenne' },
};

export default function PrestationsPage() {
  const { user } = useOutletContext() || {};

  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [metiers, setMetiers]             = useState([]);
  const [selectedMetier, setSelectedMetier] = useState(null);
  const [templates, setTemplates]         = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [myPrestations, setMyPrestations] = useState([]);
  const [showModal, setShowModal]         = useState(false);
  const [editingPrestation, setEditingPrestation] = useState(null);
  const [selectedTemplate, setSelectedTemplate]   = useState(null);
  const [stats, setStats]                 = useState(null);
  const [formData, setFormData]           = useState({
    name: '', description_short: '', description_long: '',
    tarif_type: 'sur_devis', tarif_montant: '', tarif_unite: '€',
    duree_estimee: '', zone_intervention_km: 50,
    materiaux_fournis: false, materiaux_option: false,
    certifications: [], status: 'active',
  });

  const templatesRef = useRef(null);

  useEffect(() => {
    fetchMetiers();
    fetchMyPrestations();
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedMetier) {
      fetchTemplates(selectedMetier.slug);
    }
  }, [selectedMetier]);

  const fetchMetiers = () => {
    const data = getAllMetiers();
    setMetiers(data);
    if (data.length > 0) setSelectedMetier(data[0]);
  };

  const fetchTemplates = (slug) => {
    setLoadingTemplates(true);
    const data = getPrestationsForMetier(slug);
    setTemplates(data);
    setLoadingTemplates(false);
  };

  const fetchMyPrestations = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/cockpit/prestations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMyPrestations(data.prestations || []);
      }
    } catch {} finally { setLoading(false); }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/cockpit/prestations-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  const handleSelectMetier = (metier) => {
    setSelectedMetier(metier);
    setTimeout(() => {
      templatesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const isTemplateActivated = (id) => myPrestations.some(p => p.template_id === id);
  const getActivatedPrestation = (id) => myPrestations.find(p => p.template_id === id);

  const openActivateModal = (template) => {
    setSelectedTemplate(template);
    setEditingPrestation(null);
    setFormData({
      name: template.name,
      description_short: template.description_short,
      description_long: template.description_long,
      tarif_type: template.tarif_type,
      tarif_montant: template.tarif_indicatif || '',
      tarif_unite: template.tarif_type === 'horaire' ? '€/h' : '€',
      duree_estimee: template.duree_estimee || '',
      zone_intervention_km: 50,
      materiaux_fournis: template.materiaux_fournis || false,
      materiaux_option: false,
      certifications: [], status: 'active',
    });
    setShowModal(true);
  };

  const openEditModal = (prestation) => {
    setEditingPrestation(prestation);
    setSelectedTemplate(null);
    setFormData({
      name: prestation.name,
      description_short: prestation.description_short,
      description_long: prestation.description_long,
      tarif_type: prestation.tarif_type,
      tarif_montant: prestation.tarif_montant || '',
      tarif_unite: prestation.tarif_unite || '€',
      duree_estimee: prestation.duree_estimee || '',
      zone_intervention_km: prestation.zone_intervention_km || 50,
      materiaux_fournis: prestation.materiaux_fournis || false,
      materiaux_option: prestation.materiaux_option || false,
      certifications: prestation.certifications || [],
      status: prestation.status || 'active',
    });
    setShowModal(true);
  };

  const openCreateCustomModal = () => {
    setEditingPrestation(null);
    setSelectedTemplate(null);
    setFormData({
      name: '', description_short: '', description_long: '',
      tarif_type: 'forfait', tarif_montant: '', tarif_unite: '€',
      duree_estimee: '', zone_intervention_km: 50,
      materiaux_fournis: false, materiaux_option: false,
      certifications: [], status: 'active',
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.description_short) {
      toast.error('Nom et description courte requis');
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('client_token');
    try {
      // Le backend (extra=forbid) rejette en 422 tout champ hors modèle :
      // en édition (PUT), ne pas envoyer template_id/metier/metier_slug ni
      // photos (sinon écrasement des photos existantes par [])
      const payload = editingPrestation
        ? {
            ...formData,
            tarif_montant: formData.tarif_montant ? parseFloat(formData.tarif_montant) : null,
            zone_intervention_km: parseInt(formData.zone_intervention_km) || 50,
          }
        : {
            template_id: selectedTemplate?.id || null,
            metier: selectedMetier?.name,
            metier_slug: selectedMetier?.slug,
            ...formData,
            tarif_montant: formData.tarif_montant ? parseFloat(formData.tarif_montant) : null,
            zone_intervention_km: parseInt(formData.zone_intervention_km) || 50,
            photos: [],
          };
      const url = editingPrestation
        ? `${API_URL}/api/cockpit/prestations/${editingPrestation.id}`
        : `${API_URL}/api/cockpit/prestations`;
      const res = await fetch(url, {
        method: editingPrestation ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editingPrestation ? 'Prestation mise à jour !' : 'Prestation activée !');
        setShowModal(false);
        fetchMyPrestations();
        fetchStats();
      } else {
        let msg = 'Erreur';
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        if (res.status === 401 || res.status === 403) msg = 'Non autorisé — êtes-vous bien connecté ?';
        if (res.status === 0 || !res.status) msg = 'Backend inaccessible (vérifiez la connexion)';
        toast.error(msg);
      }
    } catch (e) {
      const isNetworkError = e instanceof TypeError;
      toast.error(isNetworkError ? 'Impossible de contacter le serveur' : 'Erreur inattendue');
    } finally { setSaving(false); }
  };

  // Changement de statut via PUT /prestations/{id} : la route PATCH
  // /prestations/{id}/status n'existe pas sur le backend Railway déployé
  const changeStatus = async (id, status, successMsg) => {
    const token = localStorage.getItem('client_token');
    const res = await fetch(`${API_URL}/api/cockpit/prestations/${id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      toast.success(successMsg);
      fetchMyPrestations();
      fetchStats();
    } else {
      let msg = 'Erreur lors du changement de statut';
      try { const err = await res.json(); msg = err.detail || msg; } catch {}
      toast.error(msg);
    }
  };

  const handleDeactivate = async (id) => {
    if (!confirm('Désactiver cette prestation ?')) return;
    await changeStatus(id, 'inactive', 'Désactivée');
  };

  const handleReactivate = async (id) => {
    await changeStatus(id, 'active', 'Réactivée');
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer définitivement ?')) return;
    const token = localStorage.getItem('client_token');
    const res = await fetch(`${API_URL}/api/cockpit/prestations/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) { toast.success('Supprimée'); fetchMyPrestations(); fetchStats(); }
  };

  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert],
    }));
  };

  const prestationsByMetier = useMemo(() => {
    const grouped = {};
    myPrestations.forEach(p => {
      if (!grouped[p.metier]) grouped[p.metier] = { metier: p.metier, slug: p.metier_slug, prestations: [] };
      grouped[p.metier].prestations.push(p);
    });
    return Object.values(grouped);
  }, [myPrestations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const activePrestations = myPrestations.filter(p => p.status === 'active');

  return (
    <div className="min-h-full bg-gray-50" data-testid="prestations-page">
      {/* ── Header premium ── */}
      <div className="bg-white border-b border-gray-100 px-6 py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Mes Prestations</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Activez vos services pour apparaître dans l'annuaire AbracadaBati
              </p>
            </div>
          </div>

          {/* Statut en ligne */}
          {activePrestations.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-xl">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-green-700">
                {activePrestations.length} prestation{activePrestations.length > 1 ? 's' : ''} en ligne
              </span>
              {user?.profile_id && (
                <a
                  href={`/artisans/${user.profile_id || user.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 flex items-center gap-1 text-xs text-green-600 underline underline-offset-2 hover:text-green-800"
                >
                  Voir ma fiche <Eye className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Stats inline */}
        {stats && (
          <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">{stats.total_prestations}</span>
              <span className="text-sm text-gray-500">Total</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-600">{stats.active_prestations}</span>
              <span className="text-sm text-gray-500">Actives</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-400">{stats.inactive_prestations}</span>
              <span className="text-sm text-gray-500">Inactives</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-500" />
              <span className="text-2xl font-bold text-gray-900">{stats.max_zone_km || 50}</span>
              <span className="text-sm text-gray-500">km max</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Corps ── */}
      <div className="px-6 py-6 space-y-6">

        {/* ── Sélection métier ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Choisissez votre métier</p>
              <p className="text-sm text-gray-500 mt-0.5">{metiers.length} métiers disponibles · prestations pré-remplies</p>
            </div>
            {selectedMetier && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium"
                style={{ backgroundColor: `${selectedMetier.color}18`, color: selectedMetier.color }}
              >
                <span>{selectedMetier.icon}</span>
                <span>{selectedMetier.name}</span>
              </div>
            )}
          </div>

          {/* Grille métiers — 4 colonnes sur mobile, 8 sur desktop */}
          <div className="p-4">
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
              {metiers.map((metier) => {
                const isSelected = selectedMetier?.slug === metier.slug;
                const hasActive = myPrestations.some(p => p.metier_slug === metier.slug && p.status === 'active');
                return (
                  <button
                    key={metier.slug}
                    onClick={() => handleSelectMetier(metier)}
                    className={`relative p-3 rounded-xl border-2 text-center transition-all duration-150 group ${
                      isSelected
                        ? 'border-orange-400 bg-orange-50 shadow-sm'
                        : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50 bg-white'
                    }`}
                  >
                    {hasActive && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-green-500" title="Prestations actives" />
                    )}
                    <span className="text-2xl block mb-1">{metier.icon}</span>
                    <span className={`text-xs font-medium line-clamp-2 leading-tight ${isSelected ? 'text-orange-700' : 'text-gray-600'}`}>
                      {metier.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Templates du métier sélectionné ── */}
        <div ref={templatesRef}>
          <AnimatePresence mode="wait">
            {selectedMetier && (
              <motion.div
                key={selectedMetier.slug}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
              >
                {/* Header section templates */}
                <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                      style={{ backgroundColor: `${selectedMetier.color}18` }}
                    >
                      {selectedMetier.icon}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{selectedMetier.name}</p>
                      <p className="text-sm text-gray-500">
                        {loadingTemplates ? 'Chargement…' : `${templates.length} prestations disponibles`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={openCreateCustomModal}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Personnalisée</span>
                  </button>
                </div>

                {/* Liste des templates */}
                <div className="divide-y divide-gray-50">
                  {loadingTemplates ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="py-10 text-center text-gray-400 text-sm">
                      Aucune prestation disponible pour ce métier
                    </div>
                  ) : (
                    templates.map((template, index) => {
                      const isActivated = isTemplateActivated(template.id);
                      const activated = getActivatedPrestation(template.id);
                      return (
                        <div
                          key={template.id}
                          className={`px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors ${
                            isActivated ? 'bg-green-50/40' : ''
                          }`}
                        >
                          {/* Numéro */}
                          <span className="text-xs font-medium text-gray-400 w-5 flex-shrink-0 text-center">
                            {index + 1}
                          </span>

                          {/* Info principale */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-medium text-gray-900 text-sm">{template.name}</span>
                              {isActivated && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                  <CheckCircle className="w-3 h-3" />
                                  Activée
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 line-clamp-1">{template.description_short}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tarifTypeLabels[template.tarif_type]?.color || 'bg-slate-100 text-slate-600'}`}>
                                {tarifTypeLabels[template.tarif_type]?.icon} {tarifTypeLabels[template.tarif_type]?.label}
                                {template.tarif_indicatif ? ` ${template.tarif_indicatif}€` : ''}
                              </span>
                              {template.duree_estimee && (
                                <span className="flex items-center gap-1 text-xs text-gray-400">
                                  <Clock className="w-3 h-3" />{template.duree_estimee}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {isActivated ? (
                              <>
                                {/* Badge vert "Active" */}
                                <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-500 text-white text-xs font-semibold">
                                  <Check className="w-3 h-3" />
                                  Active
                                </span>
                                <button
                                  onClick={() => openEditModal(activated)}
                                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all"
                                  title="Modifier"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeactivate(activated.id)}
                                  className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                                  title="Désactiver"
                                >
                                  <EyeOff className="w-3.5 h-3.5" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => openActivateModal(template)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold hover:bg-orange-600 transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Activer
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Mes prestations actives ── */}
        {prestationsByMetier.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Mes prestations activées</p>
                  <p className="text-sm text-gray-500">{activePrestations.length} visible{activePrestations.length > 1 ? 's' : ''} sur l'annuaire</p>
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-50">
              {prestationsByMetier.map((group) => (
                <div key={group.slug}>
                  <div className="px-6 py-2 bg-gray-50/60">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {metiers.find(m => m.slug === group.slug)?.icon} {group.metier}
                    </span>
                  </div>
                  {group.prestations.map((p) => (
                    <div
                      key={p.id}
                      className={`px-6 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors ${
                        p.status === 'inactive' ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-medium text-gray-900 text-sm truncate">{p.name}</span>
                          {p.status === 'inactive' && (
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-500 text-xs rounded-full">Inactive</span>
                          )}
                          {p.is_demo && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs rounded-full">TEST</span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${tarifTypeLabels[p.tarif_type]?.color || 'bg-slate-100 text-slate-600'}`}>
                            {tarifTypeLabels[p.tarif_type]?.label}
                            {p.tarif_montant ? ` ${p.tarif_montant}${p.tarif_unite || '€'}` : ''}
                          </span>
                          {p.duree_estimee && (
                            <span className="flex items-center gap-1 text-gray-400">
                              <Clock className="w-3 h-3" />{p.duree_estimee}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-gray-400">
                            <MapPin className="w-3 h-3" />{p.zone_intervention_km} km
                          </span>
                          {p.certifications?.length > 0 && (
                            <span className="flex items-center gap-1 text-gray-400">
                              <Award className="w-3 h-3" />{p.certifications.length} certif.
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEditModal(p)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all" title="Modifier">
                          <Pencil className="w-4 h-4" />
                        </button>
                        {p.status === 'active' ? (
                          <button onClick={() => handleDeactivate(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all" title="Désactiver">
                            <EyeOff className="w-4 h-4" />
                          </button>
                        ) : (
                          <button onClick={() => handleReactivate(p.id)} className="p-2 rounded-lg hover:bg-green-50 text-gray-400 hover:text-green-600 transition-all" title="Réactiver">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state initial */}
        {!selectedMetier && myPrestations.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-16 text-center">
            <Wrench className="w-14 h-14 text-gray-200 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-2">Sélectionnez votre métier</h3>
            <p className="text-sm text-gray-500">Choisissez un métier ci-dessus pour découvrir les prestations disponibles</p>
          </div>
        )}
      </div>

      {/* ── Modal d'édition / activation ── */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl shadow-2xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Handle mobile */}
              <div className="sm:hidden flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-gray-200 rounded-full" />
              </div>

              {/* Header modal */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedMetier && <span className="text-2xl">{selectedMetier.icon}</span>}
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      {editingPrestation ? 'Modifier' : selectedTemplate ? 'Activer la prestation' : 'Prestation personnalisée'}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {selectedTemplate ? selectedTemplate.name : editingPrestation?.name || ''}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Body modal */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nom de la prestation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 text-sm"
                    placeholder="Ex: Installation chauffe-eau"
                  />
                </div>

                {/* Description courte */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description courte <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs text-gray-400 font-normal">({formData.description_short.length}/160)</span>
                  </label>
                  <textarea
                    maxLength={160}
                    value={formData.description_short}
                    onChange={e => setFormData({ ...formData, description_short: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm resize-none"
                    rows={2}
                    placeholder="Description affichée dans les listings"
                  />
                </div>

                {/* Description longue */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description détaillée
                    <span className="ml-2 text-xs text-gray-400 font-normal">({formData.description_long.length}/1000)</span>
                  </label>
                  <textarea
                    maxLength={1000}
                    value={formData.description_long}
                    onChange={e => setFormData({ ...formData, description_long: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm resize-none"
                    rows={3}
                    placeholder="Description complète de la prestation"
                  />
                </div>

                {/* Tarification */}
                <div className="border-t border-gray-100 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Euro className="w-4 h-4 text-gray-400" /> Tarification
                  </h3>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                    {Object.entries(tarifTypeLabels).map(([key, { label, icon, color }]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, tarif_type: key })}
                        className={`p-2.5 rounded-xl border-2 text-center transition-all text-xs ${
                          formData.tarif_type === key
                            ? 'border-orange-400 bg-orange-50'
                            : 'border-gray-100 hover:border-gray-200 bg-white'
                        }`}
                      >
                        <span className="text-lg block mb-0.5">{icon}</span>
                        <span className="font-medium leading-tight">{label}</span>
                      </button>
                    ))}
                  </div>

                  {(formData.tarif_type === 'forfait' || formData.tarif_type === 'horaire' || formData.tarif_type === 'metre_carre') && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Montant</label>
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="number" min="0" step="0.01"
                            value={formData.tarif_montant}
                            onChange={e => setFormData({ ...formData, tarif_montant: e.target.value })}
                            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm"
                            placeholder="0"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Unité</label>
                        <select
                          value={formData.tarif_unite}
                          onChange={e => setFormData({ ...formData, tarif_unite: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm"
                        >
                          <option value="€">€ (forfait)</option>
                          <option value="€/h">€/h</option>
                          <option value="€/m²">€/m²</option>
                          <option value="€/ml">€/ml</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Détails */}
                <div className="border-t border-gray-100 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" /> Détails
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Durée estimée</label>
                      <input
                        type="text"
                        value={formData.duree_estimee}
                        onChange={e => setFormData({ ...formData, duree_estimee: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm"
                        placeholder="Ex: 2-3h"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Zone d'intervention (km)</label>
                      <input
                        type="number" min="1" max="500"
                        value={formData.zone_intervention_km}
                        onChange={e => setFormData({ ...formData, zone_intervention_km: parseInt(e.target.value) || 50 })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3">
                    {[['materiaux_fournis', 'Matériaux fournis'], ['materiaux_option', 'Matériaux en option']].map(([key, lbl]) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData[key]}
                          onChange={e => setFormData({ ...formData, [key]: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-300"
                        />
                        <span className="text-sm text-gray-700">{lbl}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Certifications */}
                <div className="border-t border-gray-100 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-gray-400" /> Certifications
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(certificationLabels).map(([key, { label, description }]) => {
                      const active = formData.certifications.includes(key);
                      return (
                        <button
                          key={key} type="button"
                          onClick={() => toggleCertification(key)}
                          title={description}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                            active ? 'bg-green-100 text-green-700 border-2 border-green-300' : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:border-gray-200'
                          }`}
                        >
                          {active && <Check className="w-3 h-3" />}
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer modal */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/80 flex items-center justify-between gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.status === 'active'}
                    onChange={e => setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })}
                    className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-300"
                  />
                  <span className="text-sm text-gray-700 font-medium">Activer immédiatement</span>
                </label>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowModal(false)} className="rounded-xl">
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-6"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingPrestation ? 'Enregistrer' : 'Activer')}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
